import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Eye, X } from 'lucide-react';
import type { LerPecaResponse, MoldePageCandidate } from '../types';
import {
  buildPreviewLabel,
  getDefaultUiState,
  getPartOptions,
  getSideOptions,
  getUiStateFromCandidate,
  MOLDE_V3_PIECES,
  normalizeUiState,
  type MoldeV3UiState,
} from './catalog';

type ConfigSheetProps = {
  candidate: MoldePageCandidate | null;
  open: boolean;
  busy: boolean;
  canGoNext: boolean;
  readInfo: LerPecaResponse | null;
  onClose: () => void;
  onFocus: (candidate: MoldePageCandidate) => Promise<void> | void;
  onReadActive: () => Promise<void>;
  onApply: (state: MoldeV3UiState) => Promise<void>;
  onApplyAndNext: (state: MoldeV3UiState) => Promise<void>;
  onClear: () => Promise<void>;
};

function getReadInfoText(readInfo: LerPecaResponse) {
  if (readInfo.marcado) return `Shape ativo: ${readInfo.identificador}`;
  if (readInfo.motivo === 'nenhum_shape_selecionado') return 'Nenhum shape ativo no CorelDRAW.';
  if (readInfo.motivo === 'multiplos_shapes_selecionados') return 'Ha mais de 1 shape ativo no CorelDRAW.';
  return 'O shape ativo ainda nao possui marcacao MoldeOne.';
}

export function ConfigSheet({
  candidate,
  open,
  busy,
  canGoNext,
  readInfo,
  onClose,
  onFocus,
  onReadActive,
  onApply,
  onApplyAndNext,
  onClear,
}: ConfigSheetProps) {
  const [draft, setDraft] = useState<MoldeV3UiState>(getDefaultUiState());
  const [isRendered, setIsRendered] = useState(open);
  const [isVisible, setIsVisible] = useState(open);

  useEffect(() => {
    if (!candidate) {
      setDraft(getDefaultUiState());
      return;
    }

    setDraft(
      normalizeUiState({
        ...getUiStateFromCandidate(candidate),
        mode: 'estrutural',
        placeholder: 'nome_completo',
      })
    );
  }, [candidate]);

  const partOptions = useMemo(
    () => getPartOptions(draft.piece),
    [draft.piece]
  );
  const sideOptions = useMemo(
    () => getSideOptions(draft.piece, draft.part),
    [draft.part, draft.piece]
  );

  useEffect(() => {
    setDraft((current) => normalizeUiState(current));
  }, [draft.mode, draft.part, draft.piece]);

  useEffect(() => {
    if (open && candidate) {
      setIsRendered(true);
      const rafId = window.requestAnimationFrame(() => setIsVisible(true));
      return () => window.cancelAnimationFrame(rafId);
    }

    setIsVisible(false);
    const timeoutId = window.setTimeout(() => setIsRendered(false), 180);
    return () => window.clearTimeout(timeoutId);
  }, [candidate, open]);

  if (!isRendered || !candidate) return null;

  return (
    <div
      className={`absolute inset-0 z-30 flex items-end bg-navy-950/70 backdrop-blur-sm transition-opacity duration-200 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <button aria-label="Fechar configuracao" onClick={onClose} className="absolute inset-0" />

      <div
        className={`relative z-10 flex max-h-[78%] w-full flex-col overflow-hidden rounded-t-[22px] border border-navy-700 bg-navy-900 shadow-2xl transition-transform duration-200 ${
          isVisible ? 'translate-y-0' : 'translate-y-8'
        }`}
      >
        <div className="border-b border-navy-700 px-3 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.2em] text-navy-400">Configuracao</div>
              <h3 className="mt-0.5 text-base font-semibold text-yellow-400">{candidate.shapeName}</h3>
              <div className="mt-0.5 text-[11px] text-navy-300">
                {candidate.layerName || 'Sem camada'} - #{candidate.staticId} - {candidate.shapeType}
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-full border border-navy-600 bg-navy-800 p-1.5 text-navy-200 transition-colors hover:bg-navy-700"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
          <div className="rounded-xl border border-green-700/30 bg-green-900/10 px-3 py-2.5">
            <div className="text-[9px] uppercase tracking-[0.18em] text-green-300">Vai gravar</div>
            <div className="mt-0.5 text-[13px] font-semibold text-white">{buildPreviewLabel(draft)}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => void onFocus(candidate)}
              className="inline-flex items-center gap-2 rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-[11px] font-medium text-navy-100 transition-colors hover:bg-navy-700"
            >
              <Eye size={13} />
              Focar no Corel
            </button>
            <button
              onClick={() => void onReadActive()}
              className="inline-flex items-center gap-2 rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-[11px] font-medium text-navy-100 transition-colors hover:bg-navy-700"
            >
              Ler shape ativo (1)
            </button>
          </div>

          {readInfo && (
            <div
              className={`rounded-lg border px-2.5 py-2 text-[11px] ${
                readInfo.marcado
                  ? 'border-green-700/30 bg-green-900/10 text-green-200'
                  : 'border-navy-700 bg-navy-800 text-navy-300'
              }`}
            >
              {getReadInfoText(readInfo)}
            </div>
          )}

          <div className="rounded-xl border border-yellow-700/30 bg-yellow-900/10 px-3 py-2.5 text-[11px] text-yellow-100">
            V3 estrutural: textos e emblemas ficam no Aplicar Arte.
          </div>

          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
                Peca
              </label>
              <select
                value={draft.piece}
                onChange={(event) =>
                  setDraft((current) =>
                    normalizeUiState({
                      ...current,
                      mode: 'estrutural',
                      piece: event.target.value as MoldeV3UiState['piece'],
                    })
                  )
                }
                className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] text-white outline-none transition-colors focus:border-yellow-500"
              >
                {MOLDE_V3_PIECES.map((piece) => (
                  <option key={piece.value} value={piece.value}>
                    {piece.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
                Parte
              </label>
              <select
                value={draft.part}
                onChange={(event) =>
                  setDraft((current) =>
                    normalizeUiState({
                      ...current,
                      mode: 'estrutural',
                      part: event.target.value,
                    })
                  )
                }
                className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] text-white outline-none transition-colors focus:border-yellow-500"
              >
                {partOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-400">
                Lado
              </label>
              <select
                value={draft.side}
                onChange={(event) =>
                  setDraft((current) =>
                    normalizeUiState({
                      ...current,
                      mode: 'estrutural',
                      side: event.target.value,
                    })
                  )
                }
                disabled={sideOptions.length <= 1}
                className="w-full rounded-lg border border-navy-600 bg-navy-800 px-3 py-2.5 text-[13px] text-white outline-none transition-colors disabled:opacity-70 focus:border-yellow-500"
              >
                {sideOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="border-t border-navy-700 px-3 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => void onClear()}
              disabled={busy}
              className="rounded-lg border border-red-700/40 bg-red-900/20 px-3 py-2.5 text-[13px] font-semibold text-red-100 transition-colors hover:bg-red-900/40 disabled:opacity-50"
            >
              Limpar
            </button>
            <button
              onClick={() => void onApply(draft)}
              disabled={busy}
              className="flex-1 rounded-lg bg-yellow-500 px-4 py-2.5 text-[13px] font-semibold text-navy-950 transition-colors hover:bg-yellow-400 disabled:opacity-50"
            >
              {busy ? 'Aplicando...' : 'Aplicar'}
            </button>
            <button
              onClick={() => void onApplyAndNext(draft)}
              disabled={busy || !canGoNext}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-navy-700 text-navy-100 transition-colors hover:bg-navy-600 disabled:opacity-50"
            >
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
